import { ERROR_COPY, GLOSS_SIZE_DEAF_FIRST, GLOSS_SIZE_LEARNER, SEED_MESSAGE } from '../lib/constants';
import { signLength } from '../lib/gloss';
import type { Dialect, GlossToken, Interpretation, Message, Mode, Speed } from '../lib/types';

export interface State {
  mode: Mode;
  speed: Speed;
  dialect: Dialect;
  /** Multiplies gloss type so captions stay readable at large OS text sizes. */
  glossScale: number;
  /** The global English panel. */
  englishOpen: boolean;
  /** Per-message English disclosure. */
  openEnglish: Record<string, boolean>;
  settingsOpen: boolean;
  busy: boolean;
  err: string | null;
  draft: string;
  listening: boolean;
  simulated: boolean;
  partial: string;
  msgs: Message[];
  /** Which message owns the avatar stage. */
  activeId: string;
  /** Index of the sign currently being signed. */
  playIdx: number;
  playing: boolean;
  /**
   * Bumped every time playback (re)starts. The timer effect keys off this so a
   * replay restarts the cadence, while a speed change re-arms from the current
   * index without restarting the reply.
   */
  playRun: number;
}

export type Action =
  | { type: 'setMode'; mode: Mode }
  | { type: 'setSpeed'; speed: Speed }
  | { type: 'setDialect'; dialect: Dialect }
  | { type: 'setGlossScale'; scale: number }
  | { type: 'setEnglishOpen'; open: boolean }
  | { type: 'toggleEnglishPanel' }
  | { type: 'toggleMessageEnglish'; id: string }
  | { type: 'setSettingsOpen'; open: boolean }
  | { type: 'setDraft'; draft: string }
  | { type: 'play'; id: string; from: number }
  | { type: 'togglePlay' }
  | { type: 'step' }
  | { type: 'seek'; index: number }
  | { type: 'send'; id: string; text: string }
  | { type: 'reply'; id: string; interpretation: Interpretation }
  | { type: 'fail' }
  | { type: 'listenStart'; simulated: boolean }
  | { type: 'listenPartial'; partial: string }
  | { type: 'listenEnd' };

export const initialState: State = {
  mode: 'Learner',
  speed: 0.75,
  dialect: 'Southern',
  glossScale: 1,
  englishOpen: false,
  openEnglish: {},
  settingsOpen: false,
  busy: false,
  err: null,
  draft: '',
  listening: false,
  simulated: false,
  partial: '',
  msgs: [SEED_MESSAGE],
  activeId: SEED_MESSAGE.id,
  // The seed reply plays from sign 1 as soon as the app opens.
  playIdx: 0,
  playing: true,
  playRun: 0,
};

export function reducer(state: State, action: Action): State {
  switch (action.type) {
    case 'setMode':
      // A mode switch changes defaults, not features: Deaf-first folds English
      // away and drops the grammar notes, but every control stays available.
      return action.mode === 'Deaf-first'
        ? { ...state, mode: action.mode, englishOpen: false, openEnglish: {} }
        : { ...state, mode: action.mode };

    case 'setSpeed':
      // The timer re-arms from the current index; the reply does not restart.
      return { ...state, speed: action.speed };

    case 'setDialect':
      return { ...state, dialect: action.dialect };

    case 'setGlossScale':
      return { ...state, glossScale: action.scale };

    case 'setEnglishOpen':
      return { ...state, englishOpen: action.open };

    case 'toggleEnglishPanel':
      return { ...state, englishOpen: !state.englishOpen };

    case 'toggleMessageEnglish':
      return {
        ...state,
        openEnglish: { ...state.openEnglish, [action.id]: !state.openEnglish[action.id] },
      };

    case 'setSettingsOpen':
      return { ...state, settingsOpen: action.open };

    case 'setDraft':
      return { ...state, draft: action.draft };

    case 'play': {
      if (!tokensOf(state, action.id).length) return state;
      return {
        ...state,
        activeId: action.id,
        playIdx: action.from,
        playing: true,
        playRun: state.playRun + 1,
      };
    }

    case 'togglePlay': {
      if (state.playing) return { ...state, playing: false };
      const tokens = activeTokens(state);
      if (!tokens.length) return state;
      // Play after the end restarts from 0; otherwise resume at the next sign.
      const from = state.playIdx >= tokens.length - 1 ? 0 : state.playIdx + 1;
      return { ...state, playIdx: from, playing: true, playRun: state.playRun + 1 };
    }

    case 'step': {
      const tokens = activeTokens(state);
      const next = state.playIdx + 1;
      if (next >= tokens.length) {
        return { ...state, playing: false, playIdx: Math.max(0, tokens.length - 1) };
      }
      return { ...state, playIdx: next };
    }

    case 'seek':
      // Tapping a token pauses and pins the stage to that sign.
      return { ...state, playIdx: action.index, playing: false };

    case 'send':
      return {
        ...state,
        msgs: [...state.msgs, { id: action.id, role: 'user', english: action.text }],
        draft: '',
        busy: true,
        err: null,
      };

    case 'reply': {
      const { gloss, english, note } = action.interpretation;
      return {
        ...state,
        msgs: [...state.msgs, { id: action.id, role: 'ai', gloss, english, note }],
        busy: false,
        activeId: action.id,
        playIdx: 0,
        playing: true,
        playRun: state.playRun + 1,
      };
    }

    case 'fail':
      // A single inline row, no modal; the composer stays usable.
      return { ...state, busy: false, err: ERROR_COPY };

    case 'listenStart':
      return { ...state, listening: true, simulated: action.simulated, partial: '' };

    case 'listenPartial':
      return { ...state, partial: action.partial };

    case 'listenEnd':
      return { ...state, listening: false, simulated: false, partial: '' };

    default: {
      const exhaustive: never = action;
      return exhaustive;
    }
  }
}

/* ── selectors ─────────────────────────────────────────────────────────── */

export function tokensOf(state: State, id: string): GlossToken[] {
  return state.msgs.find((message) => message.id === id)?.gloss ?? [];
}

export function activeTokens(state: State): GlossToken[] {
  return tokensOf(state, state.activeId);
}

export function activeMessage(state: State): Message | undefined {
  return state.msgs.find((message) => message.id === state.activeId);
}

export function currentIndex(state: State): number {
  return Math.max(0, state.playIdx);
}

export function currentToken(state: State): GlossToken {
  const tokens = activeTokens(state);
  return tokens[currentIndex(state)] ?? tokens[0] ?? { sign: 'READY' };
}

export function stageSign(state: State): string {
  return state.playing || state.playIdx >= 0 ? currentToken(state).sign : 'READY';
}

export function stageSignLength(state: State): 'short' | 'medium' | 'long' {
  return signLength(stageSign(state));
}

export function stageChip(state: State): 'INTERPRETING' | 'SIGNING' | 'PAUSED' {
  if (state.busy) return 'INTERPRETING';
  return state.playing ? 'SIGNING' : 'PAUSED';
}

export function stageChipLive(state: State): boolean {
  return state.busy || state.playing;
}

export function stageCounter(state: State): string {
  const tokens = activeTokens(state);
  return tokens.length ? `SIGN ${currentIndex(state) + 1} / ${tokens.length}` : '';
}

export function playLabel(state: State): 'PLAY' | 'PAUSE' | 'REPLAY' {
  if (state.playing) return 'PAUSE';
  return state.playIdx >= activeTokens(state).length - 1 ? 'REPLAY' : 'PLAY';
}

export function glossSizePx(state: State): number {
  return state.mode === 'Deaf-first' ? GLOSS_SIZE_DEAF_FIRST : GLOSS_SIZE_LEARNER;
}

/** Grammar notes are a Learner-mode affordance. */
export function showsNotes(state: State): boolean {
  return state.mode === 'Learner';
}
